import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { fetchProfile } from '../api/profileApi';
import { useAuth } from '../auth/AuthContext';
import {
  canCreateBookingsByLoginType,
  resolveBookingRoleBlockReason,
  resolveServerBookingRoleBlock,
  type BookingRoleBlockReason,
  type ServerBookingRoleBlock,
} from '../auth/sessionBookingRole';
import { getSessionToken, getStoredLoginIdentifier } from '../auth/sessionToken';
import { ProfileCompletionSheet } from '../components/ProfileCompletionSheet';
import { ProfileBookingAuthBlockPanel } from '../components/ProfileBookingAuthBlockPanel';
import completionStyles from '../components/ProfileCompletionSheet.module.css';
import { useI18n } from '../i18n';
import type { MessageKey } from '../i18n';
import { useAppNavigation } from '../navigation/AppNavigationContext';
import { extractProfileDisplaySummary, resolveProfileRecord } from '../utils/profileDisplaySummary';
import { syncLocalBookingProfileFromSummary } from '../utils/profileBookingCompleteStorage';
import { isPrivateClientProfileReadyForBooking, privateClientCompletionPrefill } from '../utils/profileRegistrationComplete';
import { incompletePrivateClientUserInfoKeysFromSummary } from '../utils/privateClientUserInfoProblems';
import {
  patchSignedInProfileCompletion,
  persistSignedInProfileCompletionLocally,
} from '../utils/profileCompletionSave';
import {
  resolveBookingProfileAuthBlockReason,
  type BookingProfileAuthBlockReason,
} from '../utils/bookingProfileGateAuthError';

type ProfileBookingGateContextValue = {
  guardBookingAction: (action: () => void) => void;
  /** Returns true when booking may continue; otherwise opens the completion sheet. */
  ensureBookingProfileReady: (action: () => void) => Promise<boolean>;
  /** Always open the profile completion sheet (e.g. after the server rejected create). */
  promptProfileCompletion: (action: () => void) => void;
};

const ProfileBookingGateContext = createContext<ProfileBookingGateContextValue | null>(null);

function bookingRoleBlockMessageKey(reason: BookingRoleBlockReason): MessageKey {
  switch (reason) {
    case 'asset_owner':
      return 'bookings.error.roleBlock.assetOwner';
    case 'business_client':
      return 'bookings.error.roleBlock.businessClient';
    case 'profile_role':
      return 'bookings.error.roleBlock.profileRole';
    case 'server_role':
      return 'bookings.error.roleBlock.serverRole';
    default:
      return 'bookings.error.roleBlock.loginType';
  }
}

export function ProfileBookingGateProvider({ children }: { children: ReactNode }) {
  const { t } = useI18n();
  const { isLoggedIn, userLoginType, userName, logout } = useAuth();
  const { openAuth } = useAppNavigation();
  const pendingActionRef = useRef<(() => void) | null>(null);
  const silentCompletionPushRef = useRef(false);
  const [completionOpen, setCompletionOpen] = useState(false);
  const [completionRequireSubmit, setCompletionRequireSubmit] = useState(false);
  const [authBlockReason, setAuthBlockReason] = useState<BookingProfileAuthBlockReason | null>(null);
  const [roleBlockReason, setRoleBlockReason] = useState<BookingRoleBlockReason | null>(null);
  const [serverRoleBlock, setServerRoleBlock] = useState<ServerBookingRoleBlock | null>(null);
  const [serverRegistrationBlock, setServerRegistrationBlock] = useState(false);

  const signInAgainFromGate = useCallback(() => {
    setAuthBlockReason(null);
    setCompletionOpen(false);
    logout();
    openAuth('login');
  }, [logout, openAuth]);

  const closeAuthBlock = useCallback(() => {
    setAuthBlockReason(null);
    pendingActionRef.current = null;
  }, []);

  const runProfileCheck = useCallback(
    async (action: () => void, runIfReady: boolean): Promise<boolean> => {
      const loginTypeBlock = resolveBookingRoleBlockReason(userLoginType, null);
      if (loginTypeBlock) {
        pendingActionRef.current = null;
        setServerRoleBlock(null);
        setRoleBlockReason(loginTypeBlock);
        return false;
      }

      try {
        const profileData = await fetchProfile();
        const record = resolveProfileRecord(profileData);
        if (!canCreateBookingsByLoginType(userLoginType)) {
          const profileRoleBlock = resolveBookingRoleBlockReason(userLoginType, record);
          if (profileRoleBlock) {
            pendingActionRef.current = null;
            setServerRoleBlock(null);
            setRoleBlockReason(profileRoleBlock);
            return false;
          }
        }
        const serverRoleMismatch = resolveServerBookingRoleBlock(
          userLoginType,
          record,
          getSessionToken(),
        );
        if (serverRoleMismatch) {
          pendingActionRef.current = null;
          setServerRoleBlock(serverRoleMismatch);
          setRoleBlockReason('server_role');
          return false;
        }
        const loginId = getStoredLoginIdentifier();
        const summary = extractProfileDisplaySummary(
          profileData,
          userName,
          loginId,
          getSessionToken(),
        );
        syncLocalBookingProfileFromSummary(summary, loginId);
        if (
          incompletePrivateClientUserInfoKeysFromSummary(summary).length === 0 ||
          isPrivateClientProfileReadyForBooking(profileData, summary, loginId)
        ) {
          if (runIfReady) {
            action();
          }
          return true;
        }
      } catch (err) {
        const authReason = resolveBookingProfileAuthBlockReason(err);
        if (authReason) {
          pendingActionRef.current = action;
          setAuthBlockReason(authReason);
          return false;
        }
        /* fall through to completion sheet for other profile load failures */
      }

      pendingActionRef.current = action;
      setCompletionRequireSubmit(false);
      setCompletionOpen(true);
      return false;
    },
    [userLoginType, userName],
  );

  const guardBookingAction = useCallback(
    (action: () => void) => {
      if (!isLoggedIn) {
        pendingActionRef.current = action;
        openAuth('login');
        return;
      }
      void runProfileCheck(action, true);
    },
    [isLoggedIn, openAuth, runProfileCheck],
  );

  const ensureBookingProfileReady = useCallback(
    async (action: () => void): Promise<boolean> => {
      if (!isLoggedIn) {
        pendingActionRef.current = action;
        openAuth('login');
        return false;
      }
      return runProfileCheck(action, false);
    },
    [isLoggedIn, openAuth, runProfileCheck],
  );

  const promptProfileCompletion = useCallback((action: () => void) => {
    pendingActionRef.current = action;
    void (async () => {
      try {
        const profileData = await fetchProfile();
        const loginId = getStoredLoginIdentifier();
        const summary = extractProfileDisplaySummary(
          profileData,
          userName,
          loginId,
          getSessionToken(),
        );
        syncLocalBookingProfileFromSummary(summary, loginId);
        if (incompletePrivateClientUserInfoKeysFromSummary(summary).length === 0) {
          if (!silentCompletionPushRef.current) {
            silentCompletionPushRef.current = true;
            try {
              const payload = privateClientCompletionPrefill(profileData, summary, loginId);
              const result = await patchSignedInProfileCompletion(payload);
              persistSignedInProfileCompletionLocally(payload);
              if (result.savedToServer) {
                pendingActionRef.current = null;
                action();
                return;
              }
            } catch {
              /* profile fields are already complete */
            }
          }
          pendingActionRef.current = null;
          setCompletionOpen(false);
          setServerRegistrationBlock(true);
          return;
        }
      } catch {
        /* fall through to the completion sheet when the profile is actually incomplete */
      }
      setCompletionRequireSubmit(true);
      setCompletionOpen(true);
    })();
  }, [userName]);

  const resumePendingAfterLogin = useCallback(() => {
    const action = pendingActionRef.current;
    if (!action) {
      return;
    }
    void runProfileCheck(action, true);
  }, [runProfileCheck]);

  useEffect(() => {
    if (isLoggedIn && pendingActionRef.current) {
      resumePendingAfterLogin();
    }
  }, [isLoggedIn, resumePendingAfterLogin]);

  const onCompletionSuccess = useCallback(() => {
    silentCompletionPushRef.current = false;
    setCompletionOpen(false);
    setCompletionRequireSubmit(false);
    const action = pendingActionRef.current;
    pendingActionRef.current = null;
    action?.();
  }, []);

  const closeCompletion = useCallback(() => {
    silentCompletionPushRef.current = false;
    setCompletionOpen(false);
    setCompletionRequireSubmit(false);
    pendingActionRef.current = null;
  }, []);

  const closeRoleBlock = useCallback(() => {
    setRoleBlockReason(null);
    setServerRoleBlock(null);
    pendingActionRef.current = null;
  }, []);

  const closeServerRegistrationBlock = useCallback(() => {
    setServerRegistrationBlock(false);
    silentCompletionPushRef.current = false;
    pendingActionRef.current = null;
  }, []);

  return (
    <ProfileBookingGateContext.Provider
      value={{ guardBookingAction, ensureBookingProfileReady, promptProfileCompletion }}
    >
      {children}
      {completionOpen ? (
        <ProfileCompletionSheet
          onCompleted={onCompletionSuccess}
          onDismiss={closeCompletion}
          requireExplicitSubmit={completionRequireSubmit}
        />
      ) : null}
      {authBlockReason ? (
        <div className={completionStyles.backdrop} role="presentation" onClick={closeAuthBlock}>
          <div
            className={completionStyles.sheet}
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="booking-auth-block-title"
            aria-describedby="booking-auth-block-body"
            onClick={(event) => event.stopPropagation()}
          >
            <header className={completionStyles.header}>
              <h2 id="booking-auth-block-title" className={completionStyles.title}>
                {t('bookings.error.profileAuth.title')}
              </h2>
              <button
                type="button"
                className={completionStyles.close}
                onClick={closeAuthBlock}
                aria-label={t('common.close')}
              >
                ×
              </button>
            </header>
            <div className={completionStyles.body} id="booking-auth-block-body">
              <ProfileBookingAuthBlockPanel
                reason={authBlockReason}
                onDismiss={closeAuthBlock}
                onSignInAgain={signInAgainFromGate}
              />
            </div>
          </div>
        </div>
      ) : null}
      {roleBlockReason ? (
        <div className={completionStyles.backdrop} role="presentation" onClick={closeRoleBlock}>
          <div
            className={completionStyles.sheet}
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="booking-role-block-title"
            aria-describedby="booking-role-block-body"
            onClick={(event) => event.stopPropagation()}
          >
            <header className={completionStyles.header}>
              <h2 id="booking-role-block-title" className={completionStyles.title}>
                {t('bookings.error.roleBlock.title')}
              </h2>
              <button
                type="button"
                className={completionStyles.close}
                onClick={closeRoleBlock}
                aria-label={t('common.close')}
              >
                ×
              </button>
            </header>
            <div className={completionStyles.body}>
              <p id="booking-role-block-body" className={completionStyles.intro}>
                {roleBlockReason === 'server_role' && serverRoleBlock?.roleId != null
                  ? t('bookings.error.roleBlock.serverRole', { roleId: String(serverRoleBlock.roleId) })
                  : t(bookingRoleBlockMessageKey(roleBlockReason!))}
              </p>
              <p className={completionStyles.intro}>{t('bookings.error.roleNotAllowedHint')}</p>
              {serverRoleBlock?.jwtSummary ? (
                <p className={completionStyles.intro}>
                  {t('bookings.error.roleBlock.jwtClaims', { claims: serverRoleBlock.jwtSummary })}
                </p>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}
      {serverRegistrationBlock ? (
        <div className={completionStyles.backdrop} role="presentation" onClick={closeServerRegistrationBlock}>
          <div
            className={completionStyles.sheet}
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="booking-server-registration-title"
            aria-describedby="booking-server-registration-body"
            onClick={(event) => event.stopPropagation()}
          >
            <header className={completionStyles.header}>
              <h2 id="booking-server-registration-title" className={completionStyles.title}>
                {t('bookings.error.completeRegistrationServer.title')}
              </h2>
              <button
                type="button"
                className={completionStyles.close}
                onClick={closeServerRegistrationBlock}
                aria-label={t('common.close')}
              >
                ×
              </button>
            </header>
            <div className={completionStyles.body}>
              <p id="booking-server-registration-body" className={completionStyles.intro}>
                {t('bookings.error.completeRegistrationServer.body')}
              </p>
              <p className={completionStyles.intro}>{t('bookings.error.completeRegistrationServer.hint')}</p>
            </div>
          </div>
        </div>
      ) : null}
    </ProfileBookingGateContext.Provider>
  );
}

export function useProfileBookingGate(): ProfileBookingGateContextValue {
  const ctx = useContext(ProfileBookingGateContext);
  if (!ctx) {
    throw new Error('useProfileBookingGate must be used within ProfileBookingGateProvider');
  }
  return ctx;
}
